import type { LastChange } from 'components/meeting/ChangeHistory';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useMeetingDetail, useMeetingMembers } from 'hooks/useMeeting';
import { useUserInfo } from 'hooks/useAuth';
import { MeetingRepository } from 'repository/meetingRepository';
import { findKnownDestinationCenter } from 'common/api/tourApi';
import type { SubPlace } from 'components/meeting/SubPlaceModal';
import { unwrapList } from 'common/utils/apiResponse';
import { toLocalISOString } from 'common/utils/datetime';

export type PlaceStatus = 'planned' | 'confirmed' | 'skipped';

export interface PlaceItem {
    id: number | string;
    /** place 테이블 id — 사진·후기는 이 값으로 붙습니다 (없으면 열 수 없음) */
    placeId?: string | null;
    dayIndex: number;
    order: number;
    name: string;
    address?: string;
    lat?: number | null;
    lng?: number | null;
    category?: string | null;
    time?: string | null;
    status: PlaceStatus;
    /** 담은 사람 닉네임 */
    byNickname?: string | null;
}

export interface HotelInfo {
    dayIndex: number | null;
    /** place 테이블 id — 사진·후기용 */
    placeId?: string | null;
    /** meeting_sub id — 수정·삭제용. 모임에 직접 붙은 숙소는 없습니다 */
    subPlaceId?: number | null;
    name: string;
    address?: string;
    lat?: number | null;
    lng?: number | null;
    /** 담은 사람 닉네임 */
    byNickname?: string | null;
    /** 체크인 · 체크아웃 — 숙소임을 분명히 하고 몇 박인지 읽히게 합니다 */
    checkIn?: string | null;
    checkOut?: string | null;
}

export interface DayView {
    index: number;
    date: string;
    label: string;
    isSelected: boolean;
    isPassed: boolean;
    places: PlaceItem[];
    hotel: HotelInfo | null;
}

/**
 * 집결지 — 출발 전에 모이는 곳. 여행지와 다릅니다.
 * 해외여행이면 보통 한국(인천공항 등)이고, meeting.place_id 에 저장됩니다.
 */
export interface GatherInfo {
    name: string;
    address?: string;
    lat: number;
    lng: number;
    /** '오후 6:00' 같은 표시용 문자열 (meeting.meetingTime) */
    time: string | null;
    /** 마지막으로 정한 사람 */
    byNickname: string | null;
}

export type AttendanceStatus = 'confirmed' | 'pending' | 'declined';

export interface MemberView {
    userNo: number;
    name: string;
    isMe: boolean;
    isHost: boolean;
    locationSharing: boolean;
    /** confirmed=참석 · pending=미정(UNDECIDED) · declined=불참 */
    status: AttendanceStatus;
}

export interface DestinationInfo {
    latitude: number;
    longitude: number;
    name: string;
}

export interface TravelPeriod {
    days: number;
    nights: number;
    start: Date;
    end: Date;
}

export interface TripView {
    isLoading: boolean;
    meetingId: string | undefined;
    meetingType: string | null;
    title: string;
    /** 모임 이름 (invite 문구 등에 사용) */
    name: string;
    inviteCode: string | null;
    startDate: string | null;
    endDate: string | null;
    days: DayView[];
    activeDayIdx: number;
    setActiveDay: (idx: number) => void;
    members: MemberView[];
    myUserNo: number | null;
    /** 현재 사용자가 방장/생성자인지 (종료·연장 등 권한 액션 조건) */
    isCreator: boolean;
    /** 모임 종료 여부 */
    isEnded: boolean;
    /**
     * 마지막 「장소·시간」 변경 — 상세의 한 줄용.
     *
     * lastModifiedByNickname 을 쓰지 않는 이유: 그 값은 모임 이름만 고쳐도 찍혀서
     * 장소·시간이 바뀐 것처럼 보입니다. 서버가 변경 이력에서 뽑아 줍니다.
     */
    lastChange: LastChange | null;
    /** 정산 완료 시각 — 없으면 아직 정산이 남아 홈 「정산 대기 중」에 뜹니다 */
    paymentSettledAt: string | null;
    /** 정기 모임 여부 (종료 액션 비노출 등에 사용) */
    isRegular: boolean;
    globalHotel: HotelInfo | null;
    isPerDayHotel: boolean;
    /** 모임 목적지 — 집결지 좌표 (meeting.latitude/longitude · 여행 시작 집결점) */
    destination: DestinationInfo | null;
    /** 집결지 — 출발 전에 모이는 곳. 없으면 아직 안 정한 것 */
    gather: GatherInfo | null;
    /** 활성 Day 기준 실제 지리 앵커 — 추천 장소/미니지도 anchor 로 사용
     *  우선순위: 활성 Day 관광지 첫 좌표 → 활성 Day 숙박 → 이전 Day 마지막 관광지/숙박 → globalHotel → destination
     *  여행 2일째 후쿠오카에서 anchor 를 집결지(인천공항) 로 잡던 회귀 방지용 */
    activeDayAnchor: DestinationInfo | null;
    /** SubPlaces 재로드 (등록/편집/삭제 후 호출) */
    reloadSubPlaces: () => Promise<void>;
    /** SubPlaceModal 에 그대로 넘길 raw sub_place 배열 */
    rawSubPlaces: SubPlace[];
    /** SubPlaceModal 이 요구하는 여행 기간 정보 */
    travelPeriod: TravelPeriod | null;
    /** 우리 족 사진·후기를 찾는 키 (place.place_id → 없으면 외부 id).
     *  일반 모임 상세가 쓰는 placeKey 와 같은 값입니다. */
    placeKey: string | null;
    /** 여행지 지역 ID (japan · jeju …) — 이 지역 코스를 찾는 데 씁니다.
     *  destination 은 좌표와 이름만 담아서 따로 내보냅니다. */
    travelRegionId: string | null;
}

const parseDateSafe = (s: string | null | undefined): Date | null => {
    if (!s) return null;
    const t = new Date(s);
    return isNaN(t.getTime()) ? null : t;
};

const daysBetween = (start: Date, end: Date): number => {
    const ms = end.setHours(0, 0, 0, 0) - start.setHours(0, 0, 0, 0);
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
};

const HOTEL_CATEGORY = 'HOTEL';

const subPlaceToItem = (sp: SubPlace): PlaceItem => ({
    id: sp.subPlaceId,
    placeId: sp.placeId || null,
    dayIndex: Math.max(0, (sp.dayNumber ?? 1) - 1),
    order: sp.sortOrder ?? 0,
    name: sp.placeName,
    address: sp.placeAddress,
    lat: sp.latitude ?? null,
    lng: sp.longitude ?? null,
    category: sp.category,
    time: sp.visitTime || null,
    status: 'planned',
    byNickname: sp.createdByNickname || null,
});

const subPlaceToHotel = (sp: SubPlace): HotelInfo => ({
    dayIndex: sp.dayNumber != null ? Math.max(0, sp.dayNumber - 1) : null,
    placeId: sp.placeId || null,
    subPlaceId: sp.subPlaceId,
    name: sp.placeName,
    address: sp.placeAddress,
    lat: sp.latitude ?? null,
    lng: sp.longitude ?? null,
    byNickname: sp.createdByNickname || null,
    checkIn: sp.startDatetime || null,
    checkOut: sp.endDatetime || null,
});

/**
 * 숙박이 덮는 일자 범위 — 체크인 날부터 체크아웃 전날(= 묵는 밤)까지.
 *
 * 숙박은 dayNumber 없이 체크인·체크아웃 시각으로 저장됩니다. dayNumber 로만
 * 일자를 정하면 숙박은 늘 어느 날에도 속하지 않아 화면에서 사라집니다.
 *
 * 체크아웃 날은 넣지 않습니다 — 그날은 떠나는 날이지 묵는 날이 아닙니다.
 * 다만 당일치기처럼 체크인과 체크아웃이 같은 날이면 그 하루는 보여 줍니다.
 */
const hotelDayRange = (
    sp: SubPlace,
    tripStart: Date | null,
    dayCount: number,
): number[] => {
    if (sp.dayNumber != null) return [Math.max(0, sp.dayNumber - 1)];
    if (!tripStart) return [];
    const inAt = parseDateSafe(sp.startDatetime);
    if (!inAt) return [];
    const outAt = parseDateSafe(sp.endDatetime) || inAt;

    const first = daysBetween(new Date(tripStart), new Date(inAt));
    const lastRaw = daysBetween(new Date(tripStart), new Date(outAt));
    /* 체크아웃 날은 묵는 날이 아닙니다. 같은 날이면 그 하루만 */
    const last = lastRaw > first ? lastRaw - 1 : first;

    const out: number[] = [];
    for (let i = Math.max(0, first); i <= Math.min(last, dayCount - 1); i += 1) {
        out.push(i);
    }
    return out;
};

export const useTripView = (meetingId: string | undefined): TripView => {
    const { data: meetingData, status: meetingStatus } = useMeetingDetail();
    const { data: membersData } = useMeetingMembers();
    const { userNo: myUserNo } = useUserInfo();

    const [activeDayIdx, setActiveDayIdx] = useState<number>(0);
    const setActiveDay = useCallback((idx: number) => setActiveDayIdx(idx), []);

    const [subPlaces, setSubPlaces] = useState<SubPlace[]>([]);
    const [subPlacesLoading, setSubPlacesLoading] = useState(false);

    const reloadSubPlaces = useCallback(async () => {
        if (!meetingId) return;
        setSubPlacesLoading(true);
        try {
            /* 서버는 { data: { data: [...] } } 로 한 겹 더 감싸 줍니다.
               예전 코드는 res.data 가 배열인지만 봐서 늘 빈 배열이 됐고,
               그래서 여행 흐름이 항상 "아직 관광지 미정" 이었습니다
               (개발 서버에서 7건이 오는데 화면은 0개였습니다).
               다른 호출부 여덟 곳은 전부 unwrapList/res.data.data 를 씁니다. */
            const list = unwrapList<SubPlace>(await MeetingRepository.onGetSubPlaces(meetingId));
            setSubPlaces(list);
        } catch {
            setSubPlaces([]);
        } finally {
            setSubPlacesLoading(false);
        }
    }, [meetingId]);

    useEffect(() => {
        if (!meetingId) return;
        reloadSubPlaces();
    }, [meetingId, reloadSubPlaces]);

    // useMeetingDetail().data 는 { data: {...실제모임...}, meta } 형태 wrapping — 한 번 더 unwrap
    const meeting: any = (meetingData as any)?.data || meetingData || null;

    const startDate: string | null =
        meeting?.travelStartDate
        || meeting?.startDate
        || meeting?.scheduleStartDateTime
        || meeting?.schedule?.startDatetime
        || null;
    const endDate: string | null =
        meeting?.travelEndDate
        || meeting?.endDate
        || meeting?.scheduleEndDateTime
        || meeting?.schedule?.endDatetime
        || startDate;

    const { placesByDay, hotelsByDay, hasPerDayHotel, firstHotel } = useMemo(() => {
        const placeItems = subPlaces
            .filter((sp) => (sp.category || '').toUpperCase() !== HOTEL_CATEGORY)
            .map(subPlaceToItem);
        const hotelItems = subPlaces.filter((sp) => (sp.category || '').toUpperCase() === HOTEL_CATEGORY);

        const placesByDay: Record<number, PlaceItem[]> = {};
        placeItems.forEach((p) => {
            (placesByDay[p.dayIndex] = placesByDay[p.dayIndex] || []).push(p);
        });
        Object.values(placesByDay).forEach((arr) => arr.sort((a, b) => a.order - b.order));

        /* 숙박은 dayNumber 가 없고 체크인·체크아웃 시각만 있습니다.
           dayNumber 로만 일자를 정하면 숙박이 어느 날에도 속하지 않아 화면에서
           사라집니다 — 저장은 됐는데 안 보인다는 제보가 이 경로였습니다.
           묵는 밤마다 같은 숙소를 보여 줍니다. */
        const tripStart = parseDateSafe(startDate);
        const tripEnd = parseDateSafe(endDate) || tripStart;
        const dayCount = tripStart && tripEnd
            ? daysBetween(new Date(tripStart), new Date(tripEnd)) + 1
            : 0;

        const hotelsByDay: Record<number, HotelInfo> = {};
        hotelItems.forEach((sp) => {
            const h = subPlaceToHotel(sp);
            hotelDayRange(sp, tripStart, dayCount).forEach((idx) => {
                hotelsByDay[idx] = h;
            });
        });

        const distinctHotelKeys = new Set(hotelItems.map((h) => h.placeName || h.placeAddress || '').filter(Boolean));
        const hasPerDayHotel = hotelItems.length > 1 && distinctHotelKeys.size > 1;
        const firstHotel = hotelItems.length > 0 ? subPlaceToHotel(hotelItems[0]) : null;

        return { placesByDay, hotelsByDay, hasPerDayHotel, firstHotel };
    }, [subPlaces, startDate, endDate]);

    const days: DayView[] = useMemo(() => {
        if (!startDate) return [];
        const start = parseDateSafe(startDate);
        const end = parseDateSafe(endDate) || start;
        if (!start || !end) return [];

        const n = daysBetween(new Date(start), new Date(end)) + 1;
        const now = new Date();
        const result: DayView[] = [];
        for (let i = 0; i < n; i += 1) {
            const d = new Date(start);
            d.setDate(d.getDate() + i);
            /* toISOString() 을 쓰면 KST 자정이 UTC 로는 전날 15시가 되어 Day 가 하루씩
               당겨집니다. 09/28 출발이 09/27 로 표시되고, 오늘이 Day 1 로 잡혀 "진행 중"
               이 되며, 집결 시각도 지난 것으로 계산돼 미니지도가 집결지를 건너뛰었습니다.
               날짜는 로컬 벽시계 그대로 씁니다(toLocalISOString 의 주석도 같은 경고). */
            const iso = toLocalISOString(d).slice(0, 10);
            const label = `Day ${i + 1} · ${d.getMonth() + 1}/${d.getDate()}`;
            result.push({
                index: i,
                date: iso,
                label,
                isSelected: i === activeDayIdx,
                isPassed: d.getTime() < now.setHours(0, 0, 0, 0),
                places: placesByDay[i] || [],
                hotel: hotelsByDay[i] || null,
            });
        }
        return result;
    }, [startDate, endDate, activeDayIdx, placesByDay, hotelsByDay]);

    const globalHotel: HotelInfo | null = useMemo(() => {
        if (hasPerDayHotel) return null;
        if (firstHotel) return firstHotel;

        if (!meeting) return null;
        const name = meeting.hotelName || meeting.lodgingName || null;
        if (!name) return null;
        const lat = meeting.hotelLatitude ?? meeting.hotelLat;
        const lng = meeting.hotelLongitude ?? meeting.hotelLng;
        return {
            dayIndex: null,
            placeId: meeting.hotelPlaceId || null,
            name,
            address: meeting.hotelAddress || meeting.lodgingAddress || '',
            lat: lat != null ? Number(lat) : null,
            lng: lng != null ? Number(lng) : null,
        };
    }, [meeting, hasPerDayHotel, firstHotel]);

    const members: MemberView[] = useMemo(() => {
        // membersData 도 { data: [...], meta } 형태로 wrapping 되어 있음
        const raw: any[] = Array.isArray(membersData)
            ? membersData
            : Array.isArray((membersData as any)?.data)
                ? (membersData as any).data
                : [];
        const myNo = myUserNo != null ? Number(myUserNo) : 0;
        return raw.map((m: any) => {
            const attendance = String(m.attendanceStatus || 'UNDECIDED').toUpperCase();
            const status: AttendanceStatus =
                attendance === 'ATTENDING' ? 'confirmed'
                : attendance === 'NOT_ATTENDING' ? 'declined'
                : 'pending';
            const isHost = m.role === 'CREATOR' || m.role === 'HOST' || !!m.isHost;
            return {
                userNo: Number(m.userNo),
                name: m.userName || m.nickname || m.name || '',
                isMe: myNo > 0 && Number(m.userNo) === myNo,
                isHost,
                locationSharing: !!m.locationSharing,
                status,
            };
        });
    }, [membersData, myUserNo]);

    /* 집결지 — meeting.latitude/longitude 가 그 좌표입니다(위 destination 주석 참조).
       좌표가 없으면 아직 안 정한 것으로 봅니다. 보여주는 데에는 단말 GPS 가 필요 없습니다. */
    const gather: GatherInfo | null = useMemo(() => {
        if (!meeting) return null;
        const lat = Number(meeting.latitude);
        const lng = Number(meeting.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
        return {
            name: meeting.placeName || '',
            address: meeting.placeAddress || '',
            lat,
            lng,
            time: meeting.meetingTime || null,
            /* placeSetByNickname 만 씁니다 — lastModifiedByNickname 은 모임 이름만 고쳐도
               찍히므로 그걸로 '집결지를 정한 사람' 을 대신하면 엉뚱한 이름이 붙습니다.
               기록이 없던 예전 모임은 이름 없이 둡니다(추측하지 않습니다). */
            byNickname: meeting.placeSetByNickname || null,
        };
    }, [meeting]);

    const destination: DestinationInfo | null = useMemo(() => {
        if (!meeting) return null;
        // 1순위: travelDestination 이름 (예: "후쿠오카") → 로컬 매핑 lookup
        //   meeting.latitude/longitude 는 여행 시작 집결지(예: 인천공항) 좌표라 여행지 anchor 로는 부적합
        const travelDestName: string = meeting.travelDestination || '';
        const known = travelDestName ? findKnownDestinationCenter(travelDestName) : null;
        if (known && Number.isFinite(known.lat) && Number.isFinite(known.lng)) {
            return {
                latitude: known.lat,
                longitude: known.lng,
                name: travelDestName || meeting.name || '',
            };
        }
        // 2순위: meeting.latitude/longitude — 집결지 좌표 (여행 anchor 부적합하지만 최후 fallback)
        const lat = meeting.latitude != null ? Number(meeting.latitude) : null;
        const lng = meeting.longitude != null ? Number(meeting.longitude) : null;
        if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return {
            latitude: lat,
            longitude: lng,
            name: travelDestName || meeting.placeName || meeting.address || meeting.name || '',
        };
    }, [meeting]);

    const myNo = myUserNo != null ? Number(myUserNo) : 0;
    const isCreator = (() => {
        if (!myNo) return false;
        const creatorNo = meeting?.creatorNo;
        if (creatorNo != null && Number(creatorNo) === myNo) return true;
        const rawMembers: any[] = Array.isArray(membersData)
            ? membersData
            : Array.isArray((membersData as any)?.data)
                ? (membersData as any).data
                : [];
        const me = rawMembers.find((m: any) => Number(m.userNo) === myNo);
        return me?.role === 'CREATOR' || me?.role === 'HOST';
    })();

    // 활성 Day 기준 anchor — 추천 장소 호출·미니지도 focus 용
    // meeting.latitude/longitude 는 여행 시작 집결지(예: 인천공항) 라 여행 중반부터는 부적합 →
    // 활성 Day 의 실제 관광지/숙박 좌표를 우선 사용
    const activeDayAnchor: DestinationInfo | null = useMemo(() => {
        const findAnchor = (d: DayView | undefined): DestinationInfo | null => {
            if (!d) return null;
            const p = d.places.find((pp) => pp.lat != null && pp.lng != null);
            if (p && p.lat != null && p.lng != null) {
                return { latitude: p.lat, longitude: p.lng, name: p.name };
            }
            if (d.hotel && d.hotel.lat != null && d.hotel.lng != null) {
                return { latitude: d.hotel.lat, longitude: d.hotel.lng, name: d.hotel.name };
            }
            return null;
        };
        // 1) 활성 Day 안에서
        const activeAnchor = findAnchor(days[activeDayIdx]);
        if (activeAnchor) return activeAnchor;
        // 2) 이전 Day 들 역순 — 가장 최근 실제 방문 좌표
        for (let i = activeDayIdx - 1; i >= 0; i -= 1) {
            const prevAnchor = findAnchor(days[i]);
            if (prevAnchor) return prevAnchor;
        }
        // 3) 이후 Day 들 순차 — 앞으로 갈 곳
        for (let i = activeDayIdx + 1; i < days.length; i += 1) {
            const nextAnchor = findAnchor(days[i]);
            if (nextAnchor) return nextAnchor;
        }
        return null;
    }, [days, activeDayIdx]);

    const travelPeriod: TravelPeriod | null = useMemo(() => {
        const s = parseDateSafe(startDate);
        const e = parseDateSafe(endDate);
        if (!s || !e) return null;
        const ms = new Date(e).setHours(0, 0, 0, 0) - new Date(s).setHours(0, 0, 0, 0);
        const days = Math.max(0, Math.round(ms / (24 * 60 * 60 * 1000))) + 1;
        return { days, nights: Math.max(0, days - 1), start: s, end: e };
    }, [startDate, endDate]);

    return {
        isLoading: meetingStatus === 'pending' || meetingStatus === 'default' || subPlacesLoading,
        meetingId,
        meetingType: meeting?.meetingType || null,
        title: meeting?.name || meeting?.title || meeting?.meetingTitle || '',
        name: meeting?.name || meeting?.title || meeting?.meetingTitle || '',
        inviteCode: meeting?.inviteCode || null,
        isCreator,
        isEnded: !!meeting?.endedAt,
        lastChange: (meeting as any)?.lastChangedAt ? {
            by: (meeting as any).lastChangedBy ?? null,
            /* 게이트웨이가 닉네임을 URL 인코딩해 넘기는 경우가 있습니다 */
            nickname: (() => {
                const n = (meeting as any).lastChangedByNickname;
                if (!n) return null;
                try { return decodeURIComponent(n); } catch { return n; }
            })(),
            at: (meeting as any).lastChangedAt,
            targetType: (meeting as any).lastChangedTargetType ?? null,
            action: (meeting as any).lastChangedAction ?? null,
            targetLabel: (meeting as any).lastChangedTargetLabel ?? null,
        } : null,
        paymentSettledAt: meeting?.paymentSettledAt || null,
        isRegular: !!meeting?.isRegularMeeting,
        rawSubPlaces: subPlaces,
        travelPeriod,
        startDate,
        endDate,
        days,
        activeDayIdx,
        setActiveDay,
        members,
        myUserNo: myUserNo != null && Number(myUserNo) > 0 ? Number(myUserNo) : null,
        globalHotel,
        isPerDayHotel: hasPerDayHotel,
        destination,
        gather,
        activeDayAnchor,
        reloadSubPlaces,
        placeKey: meeting?.placeId || meeting?.externalPlaceId || null,
        travelRegionId: meeting?.travelRegionId || null,
    };
};
